/**
 * LocationSource — shared "rich location indicator" helpers.
 *
 * Single source of truth for the flag + city + country + Hijri-authority
 * label that surfaces wherever a date / prayer / Eid value depends on the
 * user's locale (Calendar header, Prayer hero, future LocationSourceCard).
 *
 * Per CLAUDE.md "One door, one URL per content" — the same indicator must
 * appear identically across surfaces; data lives here so any drift is a
 * one-file fix.
 *
 * Non-negotiable: NO external links. Source attribution shows the source
 * name + (in Phase 2) opens an in-app info popover. Keeps users inside
 * AWENI's Learning Castle per user directive 2026-05-16.
 */

export interface HijriAuthorityEntry {
  /** Display name of the country in English. UI may localize the cc → name. */
  country: string;
  /** Short label of the moonsighting / calculation body for the Hijri date. */
  authority: string;
  /** Emoji flag for the country (kept in data so the calendar/widget reads
   *  the same string the user sees in the location header). */
  flag: string;
}

/** Country-code → Hijri authority used by Aladhan / HijriController proxy.
 *  Keep in sync with `api/src/Controllers/HijriController.php` country map. */
export const HIJRI_AUTHORITY: Record<string, HijriAuthorityEntry> = {
  MA: { country: 'Morocco',        authority: 'HCMM moonsighting',   flag: '🇲🇦' },
  DZ: { country: 'Algeria',        authority: 'local moonsighting',  flag: '🇩🇿' },
  TN: { country: 'Tunisia',        authority: 'Mufti of the Republic', flag: '🇹🇳' },
  EG: { country: 'Egypt',          authority: 'Dar al-Iftāʾ',        flag: '🇪🇬' },
  SA: { country: 'Saudi Arabia',   authority: 'Umm al-Qura',         flag: '🇸🇦' },
  AE: { country: 'UAE',            authority: 'Umm al-Qura',         flag: '🇦🇪' },
  KW: { country: 'Kuwait',         authority: 'Umm al-Qura',         flag: '🇰🇼' },
  QA: { country: 'Qatar',          authority: 'Umm al-Qura',         flag: '🇶🇦' },
  BH: { country: 'Bahrain',        authority: 'Umm al-Qura',         flag: '🇧🇭' },
  OM: { country: 'Oman',           authority: 'local moonsighting',  flag: '🇴🇲' },
  JO: { country: 'Jordan',         authority: 'Iftāʾ',               flag: '🇯🇴' },
  TR: { country: 'Türkiye',        authority: 'Diyanet (calculated)', flag: '🇹🇷' },
  PK: { country: 'Pakistan',       authority: 'Ruet-e-Hilal',        flag: '🇵🇰' },
  IN: { country: 'India',          authority: 'local moonsighting',  flag: '🇮🇳' },
  BD: { country: 'Bangladesh',     authority: 'local moonsighting',  flag: '🇧🇩' },
  ID: { country: 'Indonesia',      authority: 'Kemenag',             flag: '🇮🇩' },
  MY: { country: 'Malaysia',       authority: 'JAKIM',               flag: '🇲🇾' },
  US: { country: 'United States',  authority: 'ISNA Fiqh Council',   flag: '🇺🇸' },
  CA: { country: 'Canada',         authority: 'ISNA Fiqh Council',   flag: '🇨🇦' },
  GB: { country: 'United Kingdom', authority: 'Moonsighting UK',     flag: '🇬🇧' },
  FR: { country: 'France',         authority: 'CFCM / regional',     flag: '🇫🇷' },
  DE: { country: 'Germany',        authority: 'regional',            flag: '🇩🇪' },
};

export interface LocationLabel {
  /** User's city (free-text from LocationService geocode). May be null even
   *  when a countryCode is set if the geocode call failed. */
  city: string | null;
  /** ISO 3166-1 alpha-2 country code, uppercase. */
  cc: string | null;
  /** "Country · authority" composite for inline display (e.g. "Morocco · HCMM moonsighting"). */
  auth: string | null;
  /** Emoji flag — null if the cc is unmapped. */
  flag: string | null;
  /** True when no country is on record; UIs should show a "set your city"
   *  prompt rather than rendering a wrong/default authority. */
  needsLocation: boolean;
}

/**
 * Read country code + city from cached LocationService state (sync).
 *
 * The Hijri date displayed across the app depends on the country authority
 * (e.g. Saudi Umm al-Qura can differ from Morocco HCMM by 1–2 days for
 * Eid/Ramadan boundaries). When no country is set, callers should surface a
 * "set your city" prompt so users know the displayed date is the offline
 * fallback and how to fix it.
 */
export function getLocationLabel(): LocationLabel {
  try {
    const raw = localStorage.getItem('aweni_location');
    if (!raw) return { city: null, cc: null, auth: null, flag: null, needsLocation: true };
    const s = JSON.parse(raw) as { city?: string | null; countryCode?: string | null };
    const cc = (s.countryCode || '').toUpperCase() || null;
    if (!cc) return { city: s.city || null, cc: null, auth: null, flag: null, needsLocation: true };
    const ha = HIJRI_AUTHORITY[cc] || null;
    return {
      city: s.city || null,
      cc,
      auth: ha ? `${ha.country} · ${ha.authority}` : `${cc} · regional`,
      flag: ha ? ha.flag : null,
      needsLocation: false,
    };
  } catch {
    return { city: null, cc: null, auth: null, flag: null, needsLocation: true };
  }
}
